import React, {useRef, useState} from "react";
import {Modal} from "react-bootstrap";
import {Scrollbars} from "react-custom-scrollbars-2";
import {useDispatch, useSelector} from "react-redux";
import ReactToPrint from "react-to-print";
import {toast} from "react-toastify";
import {
    CheckoutCurrentCart,
    PlaceTableOrder,
    RefreshTable,
    SetCartToDefault,
} from "../../../redux/actions/Cart.Action";
import {BarCodeProduct} from "../../../redux/actions/Product.Action";
import ApiConnect from "../../../Utils/ApiConnect";
import BarcodeScanner from "../../Components/BarcodeScanner";
import CartHeader from "../../Components/Cart/CartHeader";
import CurrentCartData from "../../Components/Cart/CurrentCartData";
import DiscountTotal from "../../Components/Cart/DiscountTotal";
import {INVOICE_CSS} from "../../constant"
import {ReceiptPrint} from "../Print/ReceiptPrint";
import Checkout from "./Checkout";

const TOAST_CONFIG = {
    position: "bottom-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    draggableDirection: "y",
};


const DEFAULT_MODAL_STATE = {
    checkout: false,
    print: false,
    email: false,
    sms: false,
};

export default function TableCart() {
    const dispatch = useDispatch();
    const componentRef = useRef(null);

    const CurrentCart = useSelector((s) => s.Cart.CurrentCart);
    const CurrentCartOrder = useSelector((s) => s.Cart.CurrentCart.order);
    const CompanyInfo = useSelector((s) => s.Auth.company);

    const [modals, setModals] = useState(DEFAULT_MODAL_STATE);
    const [invoice, setInvoice] = useState(null);
    const [smsContact, setSmsContact] = useState("");
    const [mailAddress, setMailAddress] = useState("");
    const [btnDisable, setBtnDisable] = useState(false);

    const openModal = (key) => setModals((m) => ({...m, [key]: true}));
    const closeModal = (key) => setModals((m) => ({...m, [key]: false}));

    const resetAll = () => {
        setModals(DEFAULT_MODAL_STATE);
        setInvoice(null);
        setSmsContact("");
        setMailAddress("");
        setBtnDisable(false);
        dispatch(SetCartToDefault());
    };

    const hasOrder = Boolean(CurrentCart.order);
    const hasOrderItems = CurrentCart.order?.length > 0;

    const handlePlaceOrder = () => {
        if (CurrentCart.length !== 0) {
            dispatch(PlaceTableOrder());
        } else {
            toast.error("Please select the table", {...TOAST_CONFIG, position: "top-right"});
        }
    };

    const handleCloseTable = () => {
        ApiConnect.delete(
            `POS/Inventory/CloseTable?orderid=${CurrentCart.order_id}&table=${CurrentCart.table}`
        )
            .then(({data}) => {
                const fn = data.status === 200 ? toast.error : toast.warning;
                fn(data.message, TOAST_CONFIG);
                if (data.status === 200) dispatch(RefreshTable());
            })
            .catch(console.error);
    };

    const handlePrintPrompt = (data = false) => {
        setInvoice(data);
        setModals({...DEFAULT_MODAL_STATE, print: true});
    };

    const sendInvoiceRequest = async (url, body, onSuccess) => {
        setBtnDisable(true);
        const Fdata = new FormData();
        Object.entries({id: invoice, ...body}).forEach(([k, v]) => Fdata.append(k, v));
        try {
            const response = await ApiConnect({method: "post", url, data: Fdata});
            const {status, message} = response.data;
            if (status === 201 || !response.data.status) {
                toast.success(message || "Sent successfully", TOAST_CONFIG);
                onSuccess();
            } else if (status === 406) {
                // form-level error — caller can handle if needed
            } else {
                toast.error(message, TOAST_CONFIG);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setBtnDisable(false);
        }
    };

    const handleSendSMS = (e) => {
        e.preventDefault();
        sendInvoiceRequest("/POS/Checkout/SendSMS", {contact: smsContact}, () =>
            closeModal("sms")
        );
    };

    const handleSendMail = (e) => {
        e.preventDefault();
        sendInvoiceRequest("/POS/Checkout/SendMail", {contact: mailAddress}, () =>
            closeModal("email")
        );
    };

    const receiptPrintNode = (
        <ReceiptPrint
            ref={componentRef}
            CurrentCartOrder={CurrentCartOrder}
            CurrentCart={CurrentCart || []}
            CompanyInfo={CompanyInfo}
        />
    );

    const printButton = (
        <ReactToPrint
            documentTitle={`${CurrentCart.order_id}${CurrentCart.table}`}
            pageStyle={INVOICE_CSS}
            copyStyles={false}
            trigger={() => (
                <button className="col w-100 btn btn-warning btn-multiline btn-w-lg align-self-center p-20">
                    <span className="material-icons-outlined fs-35">print</span>
                    <br/>
                    Print
                </button>
            )}
            content={() => componentRef.current}
        />
    );

    return (
        <aside className="col-md-4 h-100 p-0 overflow-hidden">
            <div className="card">
                <CartHeader POS/>
                <BarcodeScanner fallback={(e) => dispatch(BarCodeProduct(e))}/>
                <Scrollbars style={{height: "calc(100vh - 40ex)"}}>
                    <CurrentCartData POS/>
                </Scrollbars>
                <DiscountTotal POS/>

                <div className="d-flex h-80px col-4 w-100">
                    <button
                        className="btn btn-danger btn-lg h-20 w-50"
                        disabled={hasOrderItems}
                        onClick={handleCloseTable}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn btn-info btn-lg h-20 w-100"
                        disabled={!hasOrder}
                        onClick={handlePlaceOrder}
                    >
                        Place Order
                    </button>
                    <button
                        className="btn btn-success btn-lg h-20 w-100"
                        disabled={!hasOrder}
                        onClick={() => openModal("checkout")}
                    >
                        Pay
                    </button>
                </div>

                {/* ── Checkout modal ── */}
                <Modal show={modals.checkout} centered size="lg" scrollable onHide={() => closeModal("checkout")}>
                    <Modal.Body className="p-0 pb-2">
                        <div className="container d-none">{receiptPrintNode}</div>
                        {printButton}
                        <Checkout closeToggle={() => closeModal("checkout")}/>
                    </Modal.Body>
                    <Modal.Footer className="p-0">
                        <button
                            className="col w-100 btn btn-danger btn-multiline btn-w-lg align-self-center p-25 fs-30"
                            onClick={() => closeModal("checkout")}
                        >
                            Cancel
                        </button>
                        <button
                            className="col w-100 btn btn-success btn-multiline btn-w-lg align-self-center p-25 fs-30"
                            onClick={() => dispatch(CheckoutCurrentCart(handlePrintPrompt))}
                        >
                            Checkout
                        </button>
                    </Modal.Footer>
                </Modal>

                {/* ── Post-checkout print modal ── */}
                <Modal show={modals.print} centered size="lg" scrollable onHide={resetAll}>
                    <Modal.Body className="p-0 pb-2">
                        <div className="container d-none">{receiptPrintNode}</div>
                    </Modal.Body>
                    <Modal.Footer className="p-0">
                        <button
                            className="col w-100 btn btn-primary btn-multiline btn-w-lg align-self-center p-20"
                            onClick={() => openModal("email")}
                        >
                            <span className="material-icons-outlined fs-35">forward_to_inbox</span>
                            <br/>Mail
                        </button>
                        <button
                            className="col w-100 btn btn-info btn-multiline btn-w-lg align-self-center p-20"
                            onClick={() => openModal("sms")}
                        >
                            <span className="material-icons-outlined fs-35">textsms</span>
                            <br/>SMS
                        </button>
                        {printButton}
                        <button
                            className="col w-100 btn btn-success btn-multiline btn-w-lg align-self-center p-20"
                            onClick={resetAll}
                        >
                            <span className="material-icons-outlined fs-35">verified</span>
                            <br/>Complete
                        </button>
                    </Modal.Footer>
                </Modal>

                {/* ── SMS modal ── */}
                <Modal show={modals.sms} className="modal fade bg-theme" onHide={() => closeModal("sms")}>
                    <Modal.Header className="mb-0 pb-0">
                        <h5 className="modal-title">Send SMS Invoice</h5>
                        <button className="btn" onClick={() => closeModal("sms")}>×</button>
                    </Modal.Header>
                    <Modal.Body className="p-0">
                        <div className="container pb-10">
                            <form onSubmit={handleSendSMS}>
                                <div className="form-group">
                                    <label className="text-dark">Customer Contact</label>
                                    <input
                                        className="form-control"
                                        type="number"
                                        required
                                        maxLength="10"
                                        value={smsContact}
                                        onChange={(e) => setSmsContact(e.target.value)}
                                        placeholder="1234567890"
                                    />
                                    <br/>
                                    <button type="submit" className="w-100 btn btn-primary" disabled={btnDisable}>
                                        Send
                                    </button>
                                </div>
                            </form>
                        </div>
                    </Modal.Body>
                </Modal>

                {/* ── Email modal ── */}
                <Modal show={modals.email} className="modal fade bg-theme" onHide={() => closeModal("email")}>
                    <Modal.Header className="mb-0 pb-0">
                        <h5 className="modal-title">Send Mail Invoice</h5>
                        <button className="btn" onClick={() => closeModal("email")}>×</button>
                    </Modal.Header>
                    <Modal.Body className="p-0">
                        <div className="container pb-10">
                            <form onSubmit={handleSendMail}>
                                <div className="form-group">
                                    <label className="text-dark">Customer Email</label>
                                    <input
                                        className="form-control"
                                        type="email"
                                        placeholder="example@example.com"
                                        value={mailAddress}
                                        required
                                        onChange={(e) => setMailAddress(e.target.value)}
                                    />
                                    <br/>
                                    <button type="submit" className="w-100 btn btn-primary" disabled={btnDisable}>
                                        Send
                                    </button>
                                </div>
                            </form>
                        </div>
                    </Modal.Body>
                </Modal>
            </div>
        </aside>
    );
}